// Cuentas y sesiones (docs/cuentas-personales.md). Todos ven y cierran sus propias sesiones. Pau (titular)
// además ve quién está dentro y desde dónde, echa a cualquiera al momento, da de alta a sus socios (enlace de un
// solo uso), rehabilita a quien perdió el móvil y lee el registro de quién hizo qué.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, Copy, LogOut, UserPlus } from 'lucide-react';
import { api, ApiHttpError } from '../lib/api';
import { Aviso, Boton, Campo, Etiqueta, Insignia } from '../features/oficina/ui';
import { t } from '../i18n';

interface Sesion {
  huella: string;
  cuentaId: string | null;
  nombre: string;
  creadaEn: string;
  vistoEn: string | null;
  ip: string | null;
  navegador: string | null;
  esLaMia: boolean;
  soloLocal?: boolean;
}

interface Cuenta {
  id: string;
  usuario: string;
  nombre: string;
  rol: 'titular' | 'socio';
  altaCompletada: boolean;
  desactivada: boolean;
}

interface Evento {
  id: number;
  en: string;
  nombre: string;
  evento: string;
  detalle: string;
  agentId: string | null;
  ip: string | null;
  navegador: string | null;
}

interface Vista {
  yo: { nombre: string; rol: 'titular' | 'socio'; legado: boolean };
  contrasenaCompartida: boolean;
  cuentas: Cuenta[];
  sesiones: Sesion[];
  registro: Evento[];
}

const hora = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

/** Qué hizo, en palabras (la ruta exacta queda en el detalle, para quien la necesite). */
export function queHizo(e: Pick<Evento, 'evento' | 'detalle'>): string {
  const d = { detalle: e.detalle };
  switch (e.evento) {
    case 'entrada':
      return e.detalle === 'aparato nuevo' ? t('cuentas.ev.entradaAparatoNuevo') : e.detalle === 'contraseña compartida' ? t('cuentas.ev.entradaCompartida') : t('cuentas.ev.entrada');
    case 'entrada_tras_alta': return t('cuentas.ev.entradaTrasAlta');
    case 'entrada_fallida': return t('cuentas.ev.entradaFallida', d);
    case 'salida': return t('cuentas.ev.salida');
    case 'alta_completada': return t('cuentas.ev.altaCompletada');
    case 'invitacion': return e.detalle;
    case 'sesion_cerrada': return t('cuentas.ev.sesionCerrada');
    case 'sesiones_cerradas': return t('cuentas.ev.sesionesCerradas', d);
    case 'cuenta_desactivada': return t('cuentas.ev.cuentaDesactivada', d);
    case 'codigo_recuperacion_usado': return t('cuentas.ev.recuperacionUsada', d);
    case 'contrasena_compartida_apagada': return t('cuentas.ev.compartidaApagada');
    case 'mensaje': return t('cuentas.ev.mensaje', d);
    case 'accion': break;
    default: return e.evento;
  }
  const ruta = e.detalle;
  const falla = / → [45]\d\d$/.test(ruta) ? t('cuentas.ev.noSeHizo') : '';
  const reglas: Array<[RegExp, Parameters<typeof t>[0]]> = [
    [/\/permisos\//, 'cuentas.ev.permiso'],
    [/\/despliegues\/\d+\/decidir/, 'cuentas.ev.despliegue'],
    [/\/contrato\/aprobar/, 'cuentas.ev.aprobarPlan'],
    [/\/contrato\/cambios/, 'cuentas.ev.cambiosPlan'],
    [/\/decision/, 'cuentas.ev.pregunta'],
    [/\/message /, 'cuentas.ev.escribio'],
    [/\/contratar/, 'cuentas.ev.contrato'],
    [/\/(cerrar|despedir|forzar) /, 'cuentas.ev.cerrarAgente'],
    [/\/seguir /, 'cuentas.ev.seguir'],
    [/\/permissions\//, 'cuentas.ev.permisoHerramienta'],
    [/\/api\/cuentas\//, 'cuentas.ev.gestionCuentas'],
  ];
  const r = reglas.find(([re]) => re.test(ruta));
  return t(r ? r[1] : 'cuentas.ev.otra') + falla;
}

export function CuentasPage() {
  const [vista, setVista] = useState<Vista | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enlace, setEnlace] = useState<{ nombre: string; enlace: string; caducaEn: string } | null>(null);
  const [nuevoUsuario, setNuevoUsuario] = useState('');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      setVista(await api<Vista>('/api/cuentas'));
    } catch (e) {
      if (e instanceof ApiHttpError && e.status === 401) return window.location.assign('/login');
      setError(e instanceof ApiHttpError ? e.message : t('cuentas.errorCarga'));
    }
  }, []);

  useEffect(() => {
    void cargar();
    const id = window.setInterval(() => void cargar(), 30_000);
    return () => window.clearInterval(id);
  }, [cargar]);

  const hacer = async (clave: string, fn: () => Promise<unknown>) => {
    setError(null);
    setOcupado(clave);
    try {
      await fn();
      await cargar();
    } catch (e) {
      setError(e instanceof ApiHttpError ? e.message : t('cuentas.errorAccion'));
    } finally {
      setOcupado(null);
    }
  };

  const invitar = (usuario: string, nombre: string) =>
    hacer(`invitar-${usuario}`, async () => {
      const r = await api<{ enlace: string; caducaEn: string }>('/api/cuentas/invitar', { method: 'POST', body: { usuario, nombre } });
      setEnlace({ nombre, ...r });
    });

  const onNueva = (e: FormEvent) => {
    e.preventDefault();
    if (!nuevoUsuario.trim() || !nuevoNombre.trim()) return setError(t('cuentas.faltanDatos'));
    void invitar(nuevoUsuario.trim().toLowerCase(), nuevoNombre.trim()).then(() => {
      setNuevoUsuario('');
      setNuevoNombre('');
    });
  };

  const salir = async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    window.location.assign('/login');
  };

  if (!vista) {
    return (
      <div className="min-h-full bg-oficina-fondo p-6 text-oficina-texto2">{error ? <Aviso tono="error">{error}</Aviso> : t('cuentas.cargando')}</div>
    );
  }
  const titular = vista.yo.rol === 'titular';

  return (
    <div className="min-h-full bg-oficina-fondo px-4 py-6 text-oficina-texto" data-testid="cuentas-page">
      <div className="mx-auto max-w-4xl space-y-6">
        <header className="flex flex-wrap items-center gap-3">
          <Boton variante="fantasma" tamano="sm" icono={ArrowLeft} onClick={() => window.location.assign('/oficina')}>
            {t('cuentas.volver')}
          </Boton>
          <h1 className="flex-1 text-o-titulo2 font-semibold">{t('cuentas.titulo')}</h1>
          <span className="text-o-texto text-oficina-texto2">{t('cuentas.hasEntradoComo', { nombre: vista.yo.nombre })}</span>
          <Boton tamano="sm" icono={LogOut} onClick={() => void salir()}>
            {t('cuentas.salir')}
          </Boton>
        </header>

        {error ? <Aviso tono="error">{error}</Aviso> : null}
        {vista.contrasenaCompartida ? (
          <Aviso tono="aviso">{t('cuentas.compartidaViva')}</Aviso>
        ) : null}

        <section className="space-y-2 rounded-o-m bg-oficina-panel p-4 ring-1 ring-white/10">
          <h2 className="text-o-titulo3 font-semibold">{titular ? t('cuentas.quienEstaDentro') : t('cuentas.tusSesiones')}</h2>
          <ul className="divide-y divide-white/[0.06]" data-testid="sesiones">
            {vista.sesiones.filter((s) => !s.soloLocal).map((s) => (
              <li key={s.huella} className="flex flex-wrap items-center gap-3 py-2.5">
                <div className="min-w-[180px] flex-1">
                  <p className="font-semibold">
                    {s.nombre} {s.esLaMia ? <Insignia tono="apagado">{t('cuentas.estaSesion')}</Insignia> : null}
                  </p>
                  <p className="text-o-texto text-oficina-texto2">
                    {t('cuentas.lineaSesion', { navegador: s.navegador ?? t('cuentas.aparatoDesconocido'), ip: s.ip ?? '—', desde: hora(s.creadaEn), visto: hora(s.vistoEn ?? s.creadaEn) })}
                  </p>
                </div>
                <Boton
                  tamano="sm"
                  variante={s.esLaMia ? 'secundario' : 'peligro'}
                  cargando={ocupado === s.huella}
                  onClick={() => void hacer(s.huella, () => api(`/api/cuentas/sesiones/${s.huella}/cerrar`, { method: 'POST' }))}
                >
                  {s.esLaMia ? t('cuentas.cerrarEsta') : t('cuentas.echar')}
                </Boton>
              </li>
            ))}
            {/* Los programas automáticos del propio servidor entran cada pocos minutos:
                una sola línea, sin «Echar» (volverían a entrar solos y no son una persona). */}
            {vista.sesiones.some((s) => s.soloLocal) ? (
              <li className="py-2.5" data-testid="sesiones-programas">
                <p className="font-semibold">{t('cuentas.programas')}</p>
                <p className="text-o-texto text-oficina-texto2">
                  {t('cuentas.programasLinea', { n: vista.sesiones.filter((s) => s.soloLocal).length })}
                </p>
              </li>
            ) : null}
            {!vista.sesiones.length ? <li className="py-2 text-oficina-texto2">{t('cuentas.sinSesiones')}</li> : null}
          </ul>
        </section>

        {titular ? (
          <section className="space-y-3 rounded-o-m bg-oficina-panel p-4 ring-1 ring-white/10">
            <h2 className="text-o-titulo3 font-semibold">{t('cuentas.cuentasTitulo')}</h2>
            <ul className="divide-y divide-white/[0.06]">
              {vista.cuentas.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2 py-2.5">
                  <div className="min-w-[160px] flex-1">
                    <p className="font-semibold">
                      {c.nombre} <span className="text-o-texto font-normal text-oficina-texto2">({c.usuario})</span>
                    </p>
                    <p className="text-o-texto text-oficina-texto2">
                      {c.rol === 'titular' ? t('cuentas.titular') : t('cuentas.socio')} ·{' '}
                      {c.desactivada ? t('cuentas.desactivada') : c.altaCompletada ? t('cuentas.activa') : t('cuentas.altaPendiente')}
                    </p>
                  </div>
                  <Boton tamano="sm" cargando={ocupado === `invitar-${c.usuario}`} onClick={() => {
                    if (window.confirm(t('cuentas.confirmarEnlaceNuevo', { nombre: c.nombre }))) void invitar(c.usuario, c.nombre);
                  }}>
                    {t('cuentas.enlaceNuevo')}
                  </Boton>
                  <Boton tamano="sm" cargando={ocupado === `todas-${c.id}`} onClick={() => void hacer(`todas-${c.id}`, () => api(`/api/cuentas/${c.id}/cerrar-todas`, { method: 'POST', body: {} }))}>
                    {t('cuentas.cerrarSusSesiones')}
                  </Boton>
                  {c.rol === 'socio' && !c.desactivada ? (
                    <Boton tamano="sm" variante="peligro" cargando={ocupado === `desactivar-${c.id}`} onClick={() => {
                      if (window.confirm(t('cuentas.confirmarDesactivar', { nombre: c.nombre }))) {
                        void hacer(`desactivar-${c.id}`, () => api(`/api/cuentas/${c.id}/cerrar-todas`, { method: 'POST', body: { desactivar: true } }));
                      }
                    }}>
                      {t('cuentas.desactivar')}
                    </Boton>
                  ) : null}
                </li>
              ))}
            </ul>

            {enlace ? (
              <Aviso tono="info">
                {t('cuentas.enlaceAviso', { nombre: enlace.nombre, hasta: hora(enlace.caducaEn) })}
                <span className="mt-2 flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-black/30 px-2 py-1 text-o-mini" data-testid="enlace-alta">{enlace.enlace}</code>
                  <Boton tamano="sm" icono={Copy} onClick={() => void navigator.clipboard?.writeText(enlace.enlace)}>
                    {t('cuentas.copiar')}
                  </Boton>
                </span>
              </Aviso>
            ) : null}

            <form onSubmit={onNueva} className="flex flex-wrap items-end gap-3 border-t border-white/[0.06] pt-3">
              <div className="w-40">
                <Etiqueta titulo={t('cuentas.usuario')}>
                  <Campo value={nuevoUsuario} onChange={(e) => setNuevoUsuario(e.target.value)} placeholder={t('cuentas.ejemploUsuario')} autoCapitalize="none" />
                </Etiqueta>
              </div>
              <div className="w-48">
                <Etiqueta titulo={t('cuentas.nombre')}>
                  <Campo value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)} placeholder={t('cuentas.ejemploNombre')} />
                </Etiqueta>
              </div>
              <Boton type="submit" variante="primario" icono={UserPlus} cargando={ocupado?.startsWith('invitar-') ?? false}>
                {t('cuentas.darDeAlta')}
              </Boton>
            </form>
          </section>
        ) : null}

        {titular ? (
          <section className="space-y-2 rounded-o-m bg-oficina-panel p-4 ring-1 ring-white/10">
            <h2 className="text-o-titulo3 font-semibold">{t('cuentas.registroTitulo')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-o-texto" data-testid="registro">
                <thead className="text-oficina-texto3">
                  <tr>
                    <th className="py-1 pr-3 font-medium">{t('cuentas.cuando')}</th>
                    <th className="py-1 pr-3 font-medium">{t('cuentas.quien')}</th>
                    <th className="py-1 pr-3 font-medium">{t('cuentas.que')}</th>
                    <th className="py-1 font-medium">{t('cuentas.desde')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {vista.registro.map((e) => (
                    <tr key={e.id} title={e.detalle}>
                      <td className="whitespace-nowrap py-1.5 pr-3 text-oficina-texto2">{hora(e.en)}</td>
                      <td className="py-1.5 pr-3 font-semibold">{e.nombre}</td>
                      <td className="py-1.5 pr-3">{queHizo(e)}</td>
                      <td className="py-1.5 text-oficina-texto2">{[e.navegador, e.ip].filter(Boolean).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
